import alan from "../asset/alan.jpg";
import carmino from "../asset/carmino.jpg";
import jones from "../asset/jones.jpg";
import maycon from "../asset/maycon.jpg";
import ruziel from "../asset/ruziel.jpg";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * O TIME — a única coisa que se edita para mexer na escala.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Esta lista é a fonte de tudo: o rodízio do plantão, a distribuição de carga
 * e a escada de "quem apoia quem" saem daqui. Não existe nome fixo em nenhuma
 * tela e nenhuma outra lista para manter em sincronia.
 *
 * ── PARA TIRAR ALGUÉM DA ESCALA ────────────────────────────────────────────
 *
 *   • Apague o bloco da pessoa; ou
 *   • marque `rodizio: false` para tirá-la do plantão mas manter o contato
 *     à mão (é o caso do Jones, abaixo).
 *
 *   Em qualquer um dos dois ela some do rodízio, da distribuição e da escada
 *   de apoio de todo mundo — inclusive de quem a tinha citado em `apoio`.
 *
 * ── PARA COLOCAR ALGUÉM NA ESCALA ──────────────────────────────────────────
 *
 *   Acrescente um bloco na posição da fila em que a pessoa deve entrar. O
 *   campo `apoio` é opcional: sem ele, a escada vira "os próximos da fila".
 *
 * ── CAMPOS ─────────────────────────────────────────────────────────────────
 *
 *   id             chave curta, usada nas TROCAS logo abaixo
 *   nome           nome completo, como aparece no cartão do plantão
 *   primeiroNome   como a pessoa é chamada no dia a dia
 *   whatsapp       link de contato direto
 *   foto           imagem importada de ../asset
 *   apoio          (opcional) ordem preferida de acionamento; nomes que não
 *                  estiverem mais no time são ignorados, e quem faltar entra
 *                  no fim da escada — nunca sobra ninguém sem apoio
 *   rodizio        (opcional) `false` tira do plantão sem tirar do cadastro
 *
 * ⚠️  A ORDEM DOS BLOCOS É A ORDEM DA FILA do plantão. Mudar a ordem daqui
 *     reorganiza quem pega qual dia — mexa só quando for essa a intenção.
 *
 * ⚠️  Evite deixar o rodízio com exatamente 7 pessoas: 7 é o tamanho da
 *     semana, então cada um passaria a pegar sempre o mesmo dia da semana o
 *     ano inteiro. Com 4, 5, 6, 8 ou 9 a rotação cobre todos os dias. Os
 *     testes conferem isso e falham se acontecer.
 */
export const TIME = [
  {
    id: "alan",
    nome: "ALAN PATRICK",
    primeiroNome: "Alan",
    whatsapp: "https://wa.me/5516997275728",
    foto: alan,
    apoio: ["maycon", "ruziel", "carmino"],
  },
  {
    id: "carmino",
    nome: "CARMINO VELLUTTO",
    primeiroNome: "Carmino",
    whatsapp: "https://wa.me/5516981516604",
    foto: carmino,
    apoio: ["maycon", "alan", "ruziel"],
  },
  {
    id: "maycon",
    nome: "MAYCON VELLUDO",
    primeiroNome: "Maycon",
    whatsapp: "https://wa.me/5516997516788",
    foto: maycon,
    apoio: ["alan", "ruziel", "carmino"],
  },
  {
    id: "ruziel",
    nome: "RUZIEL ALVES",
    primeiroNome: "Ruziel",
    whatsapp: "https://wa.me/5511972526109",
    foto: ruziel,
    apoio: ["alan", "maycon", "carmino"],
  },
  {
    id: "jones",
    nome: "JONES CARVALHO",
    primeiroNome: "Jones",
    whatsapp: "https://wa.me/5516981479372",
    foto: jones,
    rodizio: false,
  },
];

/**
 * Data em que a contagem do rodízio começa. Serve só de referência para o
 * cálculo — a escala existe para trás e para frente dela.
 *
 * ⚠️ Mudar esta data desloca quem pega qual dia em toda a escala.
 */
export const INICIO_DO_RODIZIO = "2026-07-01";

/* ─────────────────────────────────────────────────────────────────────────
 * Daqui para baixo é derivado do array acima. Não há nada para manter aqui.
 * ───────────────────────────────────────────────────────────────────────── */

/** Cadastro por id, para as telas buscarem foto, nome e contato. */
export const PESSOAS = Object.fromEntries(TIME.map((p) => [p.id, p]));

/** Ids na fila do plantão, na ordem em que aparecem no array. */
export const RODIZIO = TIME.filter((p) => p.rodizio !== false).map((p) => p.id);

/**
 * Escada de acionamento de cada plantonista: quem chamar, e em que ordem,
 * quando ele não atende ou o chamado precisa de outro recurso.
 *
 * A preferência declarada em `apoio` vem primeiro, já sem quem saiu do time;
 * o resto da fila completa a escada, começando por quem vem logo depois na
 * ordem do rodízio. Assim ninguém fica sem cadeia e ninguém precisa editar
 * cinco listas para tirar uma pessoa.
 */
export const NIVEIS_APOIO = Object.fromEntries(
  RODIZIO.map((id, posicao) => {
    const seguintes = [
      ...RODIZIO.slice(posicao + 1),
      ...RODIZIO.slice(0, posicao),
    ];
    const preferidos = (PESSOAS[id].apoio || []).filter((outro) =>
      seguintes.includes(outro)
    );
    return [
      id,
      [...preferidos, ...seguintes.filter((o) => !preferidos.includes(o))],
    ];
  })
);

/**
 * Horizonte de navegação: a escala é calculada e navegável até este número de
 * anos à frente. O cálculo em si não tem limite — é uma função da data —, isso
 * só define até onde as setas do calendário deixam ir.
 */
export const HORIZONTE_ANOS = 20;

/**
 * Empurrão de um nome na virada de cada ano civil.
 *
 * Sem ele o rodízio fica bonito nos dias e nos fins de semana, mas pode ficar
 * injusto nos feriados: quando o tamanho do time divide 365 — cinco pessoas,
 * por exemplo — o rodízio volta para a mesma posição todo 1º de janeiro e a
 * mesma pessoa herda o mesmo Natal e o mesmo 7 de setembro ano após ano.
 * Deslocando um nome por ano, os feriados de data fixa circulam pelo time.
 *
 * O empurrão não pode fazer a virada do ano cair na mesma pessoa do dia 31:
 * isso aconteceria se `365 + DESLOCAMENTO_ANUAL` fosse múltiplo do tamanho do
 * time. Os testes conferem, para o time atual, que não é o caso — nem em ano
 * comum nem em bissexto.
 */
export const DESLOCAMENTO_ANUAL = 1;

/**
 * Trocas combinadas. Use para férias, viagem ou imprevisto: quem assume passa
 * a aparecer no lugar do titular e a contagem de dias acompanha a troca, então
 * a distribuição continua honesta.
 *
 * Formato: "AAAA-MM-DD": { assume: "<id>", motivo: "texto curto" }
 *
 * Exemplo:
 *   "2026-08-14": { assume: "ruziel", motivo: "Alan em viagem" },
 */
export const TROCAS = {};

export const COBERTURA = {
  titulo: "Antes das 9h, após as 18h, fins de semana e feriados",
  detalhe:
    "No horário comercial o atendimento é do suporte; o plantão cobre o que fica fora dele.",
};
